import { useTranslation } from "@localization/useTranslation";
import { ProcessDocumentRowViewModel } from "./ProcessDocument.types";
import { isNullOrWhitespace } from "@common/stringOperations";
import { FilterOption, FilterOptionType } from "@common/components/filterPopover/FilterPopover.types.ts";
import { getUniqueFilterOptions, sortByName } from "@common/hooks/filterUtils.ts";
import { useFilterState } from "@common/hooks/useFilterState.ts";
import { applyClientFilters, matchesDateFilter, withOperators } from "@common/components/filterBar/filterOperatorMatching.ts";
import { useDotLegalQuery, useDotLegalQueryClient } from "@common/hooks/dotLegalReactQuery";
import { usePanes, useRefetchWhenPaneSaves } from "../../../main/panes/usePanes";
import ProcessDocumentPane from "./processDocumentPane/ProcessDocumentPane";

export function useProcessDocuments(processId: string) {
    const queryClient = useDotLegalQueryClient();

    const url = `/processes/${processId}/documents`;
    // A process that is not created yet, like the one rendered by the template editor preview, has no documents to load.
    const { isLoading, data } = useDotLegalQuery<Array<ProcessDocumentRowViewModel>>(url, { enabled: !isNullOrWhitespace(processId) });

    function refetchDocuments() {
        queryClient.refetchQueries(url);
    }

    return {
        isLoading,
        data,
        hasDocuments: isLoading || (data ?? []).length > 0,
        refetchDocuments,
    };
}

// Kept apart from useProcessDocuments because the edit validator reads the same documents
// query; registering the refetch there too would refetch the list once per caller every time a pane saves.
export function useProcessDocumentPanes(processId: string, refetchDocuments: () => void) {
    const { open: openPane } = usePanes();
    useRefetchWhenPaneSaves(refetchDocuments);

    function openAddDocumentPane() {
        openPane(ProcessDocumentPane, { processId });
    }

    function openEditDocumentPane(document: ProcessDocumentRowViewModel) {
        openPane(ProcessDocumentPane, {
            processId,
            selectedDocument: { documentId: document.documentId, name: document.name, fileName: "", link: document.link ?? "" },
        });
    }

    return { openAddDocumentPane, openEditDocumentPane };
}

export function useProcessDocumentFilters(processId: string, documents: Array<ProcessDocumentRowViewModel> | undefined) {
    const { translateString } = useTranslation();
    const { selectFilters, booleanFilters, dateFilters, filterOperators, onFilterChange, pinnedFilterIds, onPinnedFilterIdsChange } = useFilterState(
        `process-${processId}-documents-filters`
    );
    const createdFilter = dateFilters.created ?? null;
    const qualityScanFilter = booleanFilters.qualityScan ?? "";

    const filteredDocuments = filterDocuments();

    function filterDocuments() {
        return applyClientFilters(
            documents,
            [
                { id: "name", candidate: (document) => document.name },
                { id: "document", candidate: (document) => document.fileName ?? document.link ?? "" },
            ],
            selectFilters,
            {},
            filterOperators,
            (document) => matchesDateFilter(createdFilter, document.created) && matchesQualityScanFilter(document)
        );
    }

    function matchesQualityScanFilter(document: ProcessDocumentRowViewModel) {
        if (qualityScanFilter === "") {
            return true;
        }
        const hasQualityScanResult = document.aiQualityAssuranceResult != null;
        return qualityScanFilter === hasQualityScanResult;
    }

    function getFilterOptions(): Array<FilterOption> {
        return withOperators(
            [
                {
                    id: "name",
                    label: translateString("name"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.name ?? [],
                    fixedData: getUniqueFilterOptions(documents, (document) => document.name).sort(sortByName),
                },
                {
                    id: "document",
                    label: translateString("document"),
                    type: FilterOptionType.SelectField,
                    value: selectFilters.document ?? [],
                    fixedData: getUniqueFilterOptions(documents, (document) => document.fileName ?? document.link ?? undefined).sort(sortByName),
                },
                {
                    id: "created",
                    label: translateString("created"),
                    type: FilterOptionType.DateField,
                    value: createdFilter ?? "",
                    hideNoDate: true,
                },
                {
                    id: "qualityScan",
                    label: translateString("aiQualityScan"),
                    type: FilterOptionType.Boolean,
                    value: qualityScanFilter,
                },
            ],
            filterOperators
        );
    }

    const hasActiveFilters = Object.values(selectFilters).some((values) => values.length > 0) || createdFilter !== null || qualityScanFilter !== "";

    return {
        filteredDocuments,
        getFilterOptions,
        onFilterChange,
        hasActiveFilters,
        pinnedFilterIds,
        onPinnedFilterIdsChange,
    };
}
